// Styled confirmation modal — replaces native window.confirm() in flows
// where breaking the dark UI with a system dialog is jarring. Mirrors the
// PromptModal styling for visual consistency.

import { useEffect, type ReactNode } from "react";

interface Props {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** If true, the confirm button uses the rose destructive styling. */
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmModal({
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  onConfirm,
  onCancel
}: Props) {
  // Esc dismisses; Enter confirms — common modal expectations.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
      if (e.key === "Enter") onConfirm();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel, onConfirm]);

  return (
    <>
      <div className="fixed inset-0 bg-black/70 z-50 backdrop-blur-sm" onClick={onCancel} />
      <div
        className="fixed inset-x-4 top-1/3 max-w-sm mx-auto z-50 bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl p-5 space-y-4"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
      >
        <div className="space-y-1.5">
          <h2 id="confirm-title" className="text-lg font-semibold text-slate-100">{title}</h2>
          {description && (
            <div className="text-sm text-slate-400 leading-relaxed">{description}</div>
          )}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 py-2.5 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 text-sm font-medium"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`flex-1 py-2.5 rounded-lg text-sm font-semibold ${
              destructive
                ? "bg-rose-500 text-slate-50 hover:bg-rose-400 active:bg-rose-600"
                : "bg-emerald-500 text-slate-950 hover:bg-emerald-400 active:bg-emerald-600"
            }`}
            autoFocus
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </>
  );
}
